//ANT Design
// Axios
import { instance } from './axios.instance';

import { textToRaw, formatResContent, triggersToRaw } from '../utils/dataSync';
import { fillEmptyAdminTablesBoxes } from '../utils/table';
import { mutateRemoveDeletedSectionTriggers } from '../utils/triggers';
import axios from 'axios';
import { getProposalSections } from './sections';
import { removeDuplicatesInVariableOrderArray } from '../utils/variableOrderArray';
import { gridUnit } from '../constants/gridConfig';
import { MASKS_TYPE } from '../constants/constants';
import { dockerEditorUrl, handleLocalPdfResponse } from '../utils/localPdf';
import { withWatermarkForExport } from '../utils/watermark';

export const postProposal = async ({
  currentDocument,
  currentProposal,
  boxes,
  masks,
  configuration,
  summaryConfiguration,
  gridConfiguration,
  containers,
  columns,
  rowContainers,
  variables,
  variableGroups,
  variableOrderArray,
  triggers,
}) => {
  if (!containers || !columns || !currentDocument.id) return;
  try {
    var res;
    const url =
      currentDocument.type === 'opportunity'
        ? `/proposal/${currentProposal.id}/update`
        : `/proposal/${currentProposal.id}/update`;
    // HERE ============================================
    res = await instance.post(url, {
      content: {
        boxes: textToRaw(boxes),
        configuration,
        summaryConfiguration,
        gridConfiguration,
        containers,
        columns,
        rowContainers: rowContainers ?? [],
        variables,
        variableGroups,
        variableOrderArray: removeDuplicatesInVariableOrderArray(
          variableOrderArray ?? []
        ),
        triggers: triggersToRaw(triggers, boxes),
        masks: masks.map((m) => {
          return {
            ...m,
            boxes: m.boxes ? textToRaw(m.boxes) : [],
          };
        }),
      },
    });

    if (res.status === 200) {
      return res.data;
    }
    throw new Error(`POST proposal template  status code ${res.status}`);
  } catch (error) {
    throw error;
  }
};

// Persistance granulaire collab : box et diff au format stocké (textToRaw)
export const patchBoxRequest = async ({ documentType, documentId, box }) => {
  const url =
    documentType === 'template'
      ? `/proposals/templates/${documentId}/box/${box.id}`
      : `/proposal/${documentId}/box/${box.id}`;
  const res = await instance.patch(url, { box });
  return res.data;
};

export const patchStructureRequest = async ({ documentType, documentId, diff }) => {
  const url =
    documentType === 'template'
      ? `/proposals/templates/${documentId}/structure`
      : `/proposal/${documentId}/structure`;
  const res = await instance.patch(url, { diff });
  return res.data;
};

export const fetchProposal = async (
  { id: proposalId, type },
  defaultVariablesApi
) => {
  try {
    let res = await instance.get(`/proposal/${proposalId}`);
    let { id, content, work, landscape, proposable_id } = res.data;
    if (res.status === 200) {
      if (id && content) {
        // Fill non fetched admin tables
        content = {
          ...content,
          ...(await fillEmptyAdminTablesBoxes({
            boxes: content.boxes,
            containers: content.containers,
            columns: content.columns,
            proposable_id,
            type,
            landscape,
            marginRight: content.configuration.margins.right * gridUnit,
            marginLeft: content.configuration.margins.left * gridUnit,
            header: content.masks.find(({ id }) => id === MASKS_TYPE.HEADER.id),
            footer: content.masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id),
            gridConfiguration: content.gridConfiguration,
            configuration: content.configuration,
            triggers: content.triggers,
            variableOrderArray: content.variableOrderArray,
          })),
        };

        content = formatResContent(
          content,
          defaultVariablesApi,
          type === 'template',
          landscape
        );
      }
      const sections = await getProposalSections(id);
      content.triggers = mutateRemoveDeletedSectionTriggers({
        triggers: content.triggers,
        sections,
      });

      return {
        ...res.data,
        id,
        content,
        work,
        sections,
      };
    }
    throw new Error(`GET proposal status code ${res.status}`);
  } catch (error) {
    return {
      error,
    };
  }
};

export const fetchProposals = async ({ id: proposalId, type }, variables) => {
  try {
    let res;
    if (type === 'opportunity') {
      res = await instance.get(`/opportunities/${proposalId}/proposals`);
    } else if (type === 'offer') {
      res = await instance.get(`/offers/${proposalId}/proposals`);
    }
    return res.data;
  } catch (error) {
    return {
      error,
    };
  }
};

export const fetchPdf = async ({
  currentDocument,
  currentProposal,
  boxes,
  masks,
  configuration,
  summaryConfiguration,
  gridConfiguration,
  name,
  containers,
  columns,
  rowContainers,
  landscape,
  type,
  variables,
  variableGroups,
  variableOrderArray,
  triggers,
  formIncomplete,
}) => {
  if (!containers || !columns || !currentDocument.id) return;
  try {
    const commonPayload = {
      content: {
        boxes: textToRaw(boxes),
        configuration: withWatermarkForExport(configuration, type, {
          formIncomplete,
        }),
        containers,
        columns,
        rowContainers: rowContainers ?? [],
        summaryConfiguration,
        gridConfiguration,
        variables,
        variableGroups,
        variableOrderArray,
        triggers: triggersToRaw(triggers, boxes),
        masks: masks.map((m) => {
          return {
            ...m,
            boxes: m.boxes ? textToRaw(m.boxes) : [],
          };
        }),
      },
      landscape,
      name,
      must_be_saved: type === 'archive',
      is_for_preview: type === 'preview',
    };
    // In case you want to test with local pdf generator, fill this variable
    // with the Lambda RIE invocation endpoint in your .env
    // (http://localhost:9000/2015-03-31/functions/function/invocations).
    if (process.env.REACT_APP_LOCAL_PDF_URL) {
      const result = await axios
        .post(process.env.REACT_APP_LOCAL_PDF_URL, {
          ...commonPayload,
          editor_url: dockerEditorUrl(),
        })
        .then((response) => handleLocalPdfResponse(response, type, name));
      return { ok: true, result };
    }

    const url = `/proposal/${currentProposal.id}/generate_pdf`;

    const res = await instance.post(url, commonPayload);
    if (res.status === 202) {
      return res.data;
    } else {
      throw new Error(`POST proposal status code ${res.status}`);
    }
  } catch (error) {
    throw error;
  }
};

// 202 : la compression est asynchrone, le résultat ({ pdf_url, compressed }
// ou { pdf_error }) arrive ensuite via le websocket EditorsChannel.
export const compressPdf = async ({ currentDocument, currentProposal }) => {
  const url =
    currentDocument.type === 'template'
      ? `/proposal/templates/${currentDocument.id}/compress_pdf`
      : `/proposal/${currentProposal.id}/compress_pdf`;
  const res = await instance.post(url);
  if (res.status === 202) {
    return res.data;
  }
  throw new Error(`POST compress_pdf status code ${res.status}`);
};
